import React from 'react';
import { RefreshCw, AlertCircle, CheckCircle, XCircle } from 'lucide-react';

/**
 * API Loading State Component
 * Shows loading, success, error, and retry states for API calls
 */
const ApiLoadingState = ({
  isLoading = false,
  error = null,
  data = null,
  loadingText = "Loading...",
  errorText = "Something went wrong",
  successText = "Data loaded successfully",
  emptyText = "No data available",
  onRetry = null,
  onRefresh = null,
  showSuccess = false,
  showEmpty = true,
  className = "",
  size = "default", // "small", "default", "large"
  variant = "default" // "default", "minimal", "card"
}) => {
  // Determine current state
  const hasError = error && !isLoading;
  const hasData = data && !isLoading && !error;
  const isEmpty = !isLoading && !error && (!data || (Array.isArray(data) && data.length === 0));
  const isSuccess = !isLoading && !error && data && showSuccess;

  // Size classes
  const sizeClasses = {
    small: "text-sm",
    default: "text-base",
    large: "text-lg"
  };

  // Variant classes
  const variantClasses = {
    default: "p-4 rounded-lg border",
    minimal: "p-2",
    card: "p-6 rounded-lg border shadow-lg bg-white"
  };

  // Icon sizes
  const iconSizes = {
    small: 16,
    default: 20,
    large: 24
  };

  // Loading state
  if (isLoading) {
    return (
      <div className={`flex items-center justify-center ${variantClasses[variant]} ${className}`}>
        <div className="flex items-center space-x-3">
          <div className="animate-spin">
            <RefreshCw size={iconSizes[size]} className="text-emerald-600" />
          </div>
          <span className={`text-gray-600 ${sizeClasses[size]}`}>
            {loadingText}
          </span>
        </div>
      </div>
    );
  }

  // Error state
  if (hasError) {
    return (
      <div className={`flex flex-col items-center justify-center space-y-3 ${variantClasses[variant]} border-red-200 bg-red-50 ${className}`}>
        <div className="flex items-center space-x-2 text-red-600">
          <XCircle size={iconSizes[size]} />
          <span className={`font-medium ${sizeClasses[size]}`}>
            {errorText}
          </span>
        </div>
        
        {error?.message && (
          <p className="text-sm text-red-500 text-center max-w-md">
            {error.message}
          </p>
        )}
        
        <div className="flex space-x-2">
          {onRetry && (
            <button
              onClick={onRetry}
              className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 transition-colors"
            >
              Try Again
            </button>
          )}
          
          {onRefresh && (
            <button
              onClick={onRefresh}
              className="px-3 py-1.5 bg-gray-600 text-white text-sm rounded-md hover:bg-gray-700 transition-colors"
            >
              Refresh
            </button>
          )}
        </div>
      </div>
    );
  }

  // Success state
  if (isSuccess) {
    return (
      <div className={`flex items-center justify-center space-x-2 ${variantClasses[variant]} border-green-200 bg-green-50 ${className}`}>
        <CheckCircle size={iconSizes[size]} className="text-green-600" />
        <span className={`text-green-700 ${sizeClasses[size]}`}>
          {successText}
        </span>
      </div>
    );
  }

  // Empty state
  if (isEmpty && showEmpty) {
    return (
      <div className={`flex flex-col items-center justify-center space-y-3 ${variantClasses[variant]} border-gray-200 bg-gray-50 ${className}`}>
        <div className="flex items-center space-x-2 text-gray-500">
          <AlertCircle size={iconSizes[size]} />
          <span className={`${sizeClasses[size]}`}>
            {emptyText}
          </span>
        </div>
        
        {onRefresh && (
          <button
            onClick={onRefresh}
            className="px-3 py-1.5 bg-emerald-600 text-white text-sm rounded-md hover:bg-emerald-700 transition-colors"
          >
            Refresh
          </button>
        )}
      </div>
    );
  }

  // Default state - render children or null
  return null;
};

/**
 * Inline loading spinner for small loading states
 */
export const LoadingSpinner = ({ size = "default", className = "" }) => {
  const spinnerSizes = {
    small: 16,
    default: 20,
    large: 24
  };

  return (
    <div className={`animate-spin ${className}`}>
      <RefreshCw size={spinnerSizes[size]} className="text-emerald-600" />
    </div>
  );
};

/**
 * Error display component for inline error states
 */
export const ErrorDisplay = ({ 
  error, 
  onRetry, 
  size = "default", 
  className = "",
  showMessage = true 
}) => {
  if (!error) return null;

  const sizeClasses = {
    small: "text-xs",
    default: "text-sm",
    large: "text-base"
  };

  return (
    <div className={`flex items-center space-x-2 text-red-600 ${className}`}>
      <XCircle size={size === "small" ? 14 : size === "large" ? 20 : 16} />
      {showMessage && (
        <span className={sizeClasses[size]}>
          {error.message || "An error occurred"}
        </span>
      )}
      {onRetry && (
        <button
          onClick={onRetry}
          className="text-xs text-red-700 underline hover:no-underline"
        >
          Retry
        </button>
      )}
    </div>
  );
};

export default ApiLoadingState;
